/**
 * Per-user saved report view — which columns, in which order.
 *
 * Same two-tier persistence the inbox uses for its column preference:
 *
 *   localStorage  — read synchronously on mount so the table paints in the
 *                   user's arrangement with no flash of the default layout.
 *   API           — the durable copy on `users.report_view`, so the view
 *                   follows the user across devices. Fetched after mount and
 *                   allowed to overwrite the local copy, because the server is
 *                   the shared source of truth.
 *
 * Writes go to both: localStorage synchronously, the API best-effort. A failed
 * save is deliberately silent — losing a column arrangement is not worth an
 * error toast in the middle of reading a report, and the next successful save
 * repairs it.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../api/client.js'

interface UseReportViewResult {
  /** Visible column keys, in display order. */
  columns: string[]
  /** Replace the whole arrangement and persist it. */
  setColumns: (next: string[]) => void
  /** Local-only update — for drag-reorder, which fires on every hover. */
  setColumnsLocal: (updater: (prev: string[]) => string[]) => void
  /** Persist whatever is currently local. Call on drag end. */
  commit: () => void
  /** True until the server copy has been read (or has failed). */
  loading: boolean
}

export function useReportView(
  reportId: string,
  defaultColumns: string[],
  sanitize: (saved: unknown) => string[],
): UseReportViewResult {
  const storageKey = `kc3_report_view:${reportId}`

  const [columns, setColumnsState] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(storageKey)
      return raw ? sanitize(JSON.parse(raw)) : defaultColumns
    } catch {
      return defaultColumns
    }
  })
  const [loading, setLoading] = useState(true)

  // Sanitize identity is not stable across renders (callers pass a module
  // function, but a caller could inline one), so it is held in a ref rather
  // than listed as a dependency — a new function identity must not refetch.
  const sanitizeRef = useRef(sanitize)
  sanitizeRef.current = sanitize

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const res = await api.get<{ columns: string[] }>(
          `/agent/me/report-view?report=${encodeURIComponent(reportId)}`,
        )
        if (cancelled) return
        // An empty array means "never saved a view" — keep the defaults
        // rather than rendering a table with no columns.
        if (Array.isArray(res.columns) && res.columns.length > 0) {
          const next = sanitizeRef.current(res.columns)
          setColumnsState(next)
          try { localStorage.setItem(storageKey, JSON.stringify(next)) } catch { /* preference only */ }
        }
      } catch {
        /* offline or forbidden — the local copy stands */
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [reportId, storageKey])

  const persist = useCallback((next: string[]): void => {
    try { localStorage.setItem(storageKey, JSON.stringify(next)) } catch { /* preference only */ }
    void api.put('/agent/me/report-view', { report: reportId, columns: next })
      .catch(() => { /* best-effort */ })
  }, [reportId, storageKey])

  const setColumns = useCallback((next: string[]): void => {
    setColumnsState(next)
    persist(next)
  }, [persist])

  const setColumnsLocal = useCallback((updater: (prev: string[]) => string[]): void => {
    setColumnsState(updater)
  }, [])

  const commit = useCallback((): void => {
    setColumnsState((prev) => { persist(prev); return prev })
  }, [persist])

  return { columns, setColumns, setColumnsLocal, commit, loading }
}
